import { Outlet } from 'react-router-dom'
import { AdminPanelSettings, Hub, List, Lock, ManageHistory, Psychology, Public } from '@mui/icons-material'
import { Alert, AlertTitle, Button, Collapse } from '@mui/material'
import { com } from '@eidu/web'
import { useQueryClient } from '@tanstack/react-query'
import useEntityTypes, { useSortedEntityTypes } from '../io/useEntityTypes'
import { entityTypeKeys } from '../io/queryKeys'
import SnackBarProvider from '../components/feedback/SnackBarProvider'
import DrawerScaffold, { DrawerItem, DrawerSection } from '../components/layout/DrawerScaffold'
import { iconForEntityTypeKind } from './entity/kindViewRegistry'
import { isAdminUserType } from '../util/entityType/isAdminUserType'
import useHasGlobalPermissions from './permission/useHasGlobalPermissions'
import { buildSidebarSectionsFromEntityTypes } from '../components/navigation/SidebarSection'
import type { SidebarGroup } from '../domain/kindRegistry'
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import EntityType = com.eidu.sharedlib.entity.type.EntityType

const SIDEBAR_GROUP_ORDER: readonly SidebarGroup[] = ['entity', 'document', 'asset']

const LandingPage = () => {
  const queryClient = useQueryClient()

  const { hasPermission: hasGlobalPermission, error: fetchPermissionsError } = useHasGlobalPermissions(
    GlobalAction.ManageForms,
    GlobalAction.ManageEntityTypes,
    GlobalAction.ManagePermissionGrants,
    GlobalAction.ManageAuthentication,
    GlobalAction.ViewAuditLog
  )

  const { error } = useEntityTypes()
  const entityTypes = useSortedEntityTypes() ?? []

  if (error) {
    return (
      <SnackBarProvider>
        <Collapse in={!!error}>
          <Alert
            variant="filled"
            severity="error"
            sx={{ margin: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => queryClient.invalidateQueries({ queryKey: entityTypeKeys.all })}
              >
                Refresh
              </Button>
            }
          >
            <AlertTitle>Failed to load entities. Please try again.</AlertTitle>
            <div>{String(error)}</div>
          </Alert>
        </Collapse>
      </SnackBarProvider>
    )
  }

  if (fetchPermissionsError) {
    return (
      <SnackBarProvider>
        <Collapse in={!!fetchPermissionsError}>
          <Alert
            variant="filled"
            severity="error"
            sx={{ margin: 2 }}
            action={
              <Button color="inherit" size="small" onClick={async () => window.location.reload()}>
                Refresh
              </Button>
            }
          >
            <AlertTitle>Failed to load permissions. Please reload the page.</AlertTitle>
            <div>{String(fetchPermissionsError)}</div>
          </Alert>
        </Collapse>
      </SnackBarProvider>
    )
  }

  const createEntityTypeItem = (
    entityType: EntityType,
    icon = iconForEntityTypeKind(entityType.kind, <List />)
  ): DrawerItem => ({
    icon,
    text: entityType.name,
    key: `type-${entityType.id.toString()}`,
    path: `/entities/${entityType.id.toString()}`,
  })

  const canManageEntityTypes = hasGlobalPermission(GlobalAction.ManageEntityTypes)
  const canManagePermissionGrants = hasGlobalPermission(GlobalAction.ManagePermissionGrants)
  const canManageAdminUsers = hasGlobalPermission(GlobalAction.ManageAuthentication)
  const canViewAuditLog = hasGlobalPermission(GlobalAction.ViewAuditLog)

  const adminUsersEntityType = entityTypes.find(
    (type) => type.kind.equals(EntityTypeKind.Companion.User) && isAdminUserType(type.name)
  )

  const adminItems: DrawerItem[] = []
  if (canManageEntityTypes) {
    adminItems.push({
      icon: <Hub />,
      text: 'Types',
      key: 'types',
      path: `/types`,
    })
    adminItems.push({
      icon: <Psychology />,
      text: 'Semantics',
      key: 'semantics',
      path: `/semantics`,
    })
  }
  if (canManagePermissionGrants) {
    adminItems.push({
      icon: <Lock />,
      text: 'Access Control',
      key: 'access-control',
      path: `/access-control`,
    })
  }
  adminItems.push({
    icon: <Public />,
    text: 'Asset Registry',
    key: 'registry',
    path: `/registry`,
  })
  if (canViewAuditLog) {
    adminItems.push({
      icon: <ManageHistory />,
      text: 'Audit Log',
      key: 'audit',
      path: `/audit`,
    })
  }
  if (canManageAdminUsers && adminUsersEntityType) {
    adminItems.push({
      icon: <AdminPanelSettings />,
      text: 'Admin Users',
      key: `admin-user-${adminUsersEntityType.id.toString()}`,
      path: `/entities/${adminUsersEntityType.id.toString()}`,
    })
  }

  const adminEntityTypeId = adminUsersEntityType?.id.toString()
  const visibleEntityTypes = adminEntityTypeId
    ? entityTypes.filter((type) => type.id.toString() !== adminEntityTypeId)
    : entityTypes

  const kindSections = buildSidebarSectionsFromEntityTypes(visibleEntityTypes, createEntityTypeItem)

  const sections: DrawerSection[] = []

  if (adminItems.length) {
    sections.push({
      id: 'administration',
      title: 'Administration',
      items: adminItems,
      dividerBefore: false,
    })
  }

  for (const sidebarGroup of SIDEBAR_GROUP_ORDER) {
    const groupSections = kindSections.filter((s) => s.sidebarGroup === sidebarGroup && s.items.length > 0)
    groupSections.forEach(({ id, title, items }, index) => {
      sections.push({ id, title, items, dividerBefore: index === 0 && sections.length > 0 })
    })
  }

  return (
    <SnackBarProvider>
      <DrawerScaffold sections={sections}>
        <Outlet />
      </DrawerScaffold>
    </SnackBarProvider>
  )
}

export default LandingPage
